import { Fragment, type ReactNode } from 'react';

import { Backdrop } from '@shared/atoms/backdrop';

import { cn } from '@playbook/utils';

import { PortalNavigation } from '@portal/views/_shared/navigation';

import css from './styles.module.css';

export type PageWidth = 'narrow' | 'wide';

interface PortalPageProps {
	/** `narrow` is a form, `wide` a listing or a record. */
	width?: PageWidth;

	children: ReactNode;
}

/**
 *	The chrome every signed-in page shares. There is no root layout in this
 *	app — each view renders its own backdrop and navigation — so this stands in
 *	for one across the key pages.
 */
export function PortalPage({
	width = 'wide',

	children,
}: PortalPageProps): ReactNode {
	return (
		<Fragment>
			<Backdrop />
			<PortalNavigation variant="sticky" />
			<main className={css.main}>
				<div className={cn(css.column, css[width])}>{children}</div>
			</main>
		</Fragment>
	);
}

interface PageHeaderProps {
	title: string;
	description?: string;

	/** A back link, rendered above the title. */
	breadcrumb?: ReactNode;
	/** Sits beside the title at the top of the page. */
	badge?: ReactNode;
	/** The page's primary action, right-aligned on a wide screen. */
	actions?: ReactNode;
}

export function PageHeader({
	title,
	description,

	breadcrumb,
	badge,
	actions,
}: PageHeaderProps): ReactNode {
	return (
		<header className={css.header}>
			{breadcrumb}
			<div className={css.headline}>
				<div className={css.title}>
					<h1>{title}</h1>
					{badge}
				</div>
				{actions && <div className={css.actions}>{actions}</div>}
			</div>
			{description && <p className={css.description}>{description}</p>}
		</header>
	);
}

interface PageSectionProps {
	title?: string;
	description?: string;

	children: ReactNode;
}

export function PageSection({
	title,
	description,

	children,
}: PageSectionProps): ReactNode {
	return (
		<section className={css.section}>
			{title && <h2 className={css.sectionTitle}>{title}</h2>}
			{description && <p className={css.description}>{description}</p>}
			{children}
		</section>
	);
}
